import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/feature/PageHeader";
import StartProjectCTA from "@/components/feature/StartProjectCTA";
import { BleedSection, BleedSectionMobile } from "@/components/feature/BleedSection";

const IMG_STUDIO = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_studio-principal-at-work.webp";

const CAPABILITIES = [
  { num: "01", title: "Design", body: "Concept, planning and interior architecture led by our own studio." },
  { num: "02", title: "Interior Architecture", body: "Millwork, joinery, stone and lighting resolved as one integrated detail set." },
  { num: "03", title: "Construction", body: "In-house construction management with a network of long-term trade partners." },
  { num: "04", title: "Project Management", body: "One accountable team from feasibility through handover and aftercare." },
];

const VALUES = [
  { title: "Restraint", body: "The best rooms are edited. We design toward calm, not accumulation." },
  { title: "Craft", body: "Every joint, edge and transition is resolved to the same standard as the plan." },
  { title: "Accountability", body: "The team that draws the project is the team that builds it." },
];

export default function StudioPage() {
  const philosophy = (
    <>
      <h3 className="font-serif-display leading-[1.06]"
          style={{ fontSize: "clamp(28px,2.4vw,38px)", letterSpacing: "-0.018em", color: "#1D1A17" }}>
        We design homes we would want to live in.
      </h3>
      <p className="mt-6 text-ink-soft leading-[1.7]" style={{ fontSize: "15px" }}>
        Architectural, warm, and quietly detailed. The design-build model is not a marketing position — it is how we make sure the finished room matches the drawing.
      </p>
      <p className="mt-4 text-ink-soft leading-[1.7]" style={{ fontSize: "15px" }}>
        One studio. One accountable team. From the first sketch to the last punch item.
      </p>
    </>
  );

  return (
    <div className="min-h-screen bg-warm-bg text-ink">
      <SiteNav />
      <PageHeader
        eyebrow="Studio"
        title={<>A design + build studio, quietly integrated.</>}
        intro="Atelier Ridge is a residential design-build studio composing calm, crafted homes across Southern California — a single team combining design, interior architecture, construction and project management."
        crumbs={[{ label: "Home", to: "/" }, { label: "Studio" }]}
      />

      <BleedSection eyebrow="Philosophy" imgSrc={IMG_STUDIO}
        imgAlt="Studio interior with drawings and material samples"
        imgSide="right" bg="#F8F8F8">
        {philosophy}
      </BleedSection>
      <BleedSectionMobile eyebrow="Philosophy" imgSrc={IMG_STUDIO}
        imgAlt="Studio interior with drawings and material samples" bg="#F8F8F8">
        {philosophy}
      </BleedSectionMobile>

      <section className="w-full py-24 md:py-28">
        <div className="w-full px-5 md:px-[clamp(32px,5vw,96px)]">
          <span className="eyebrow">Capabilities</span>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
            {CAPABILITIES.map(c => (
              <div key={c.num} className="border-t hairline py-8 flex gap-6">
                <span className="eyebrow" style={{ minWidth: "28px" }}>{c.num}</span>
                <div>
                  <div className="font-serif-display font-light" style={{ fontSize: "24px" }}>{c.title}</div>
                  <p className="mt-2 text-ink-soft leading-[1.65]" style={{ fontSize: "15px" }}>{c.body}</p>
                </div>
              </div>
            ))}
            <div className="border-t hairline md:col-span-2" />
          </div>
        </div>
      </section>

      <section className="w-full pb-24 md:pb-28" style={{ backgroundColor: "#1A1A1A" }}>
        <div className="w-full pt-24 md:pt-28 px-5 md:px-[clamp(32px,5vw,96px)]">
          <span className="eyebrow" style={{ color: "rgba(244,238,230,0.6)" }}>Approach & Values</span>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-10">
            {VALUES.map(v => (
              <div key={v.title} className="border-t pt-8" style={{ borderColor: "rgba(244,238,230,0.2)" }}>
                <div className="font-serif-display font-light" style={{ fontSize: "28px", color: "#F4EEE6" }}>{v.title}</div>
                <p className="mt-4 leading-[1.65]" style={{ fontSize: "15px", color: "rgba(244,238,230,0.78)" }}>{v.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 pt-10 border-t" style={{ borderColor: "rgba(244,238,230,0.2)" }}>
            <p className="font-serif-display italic font-light leading-[1.3] max-w-[720px]"
               style={{ fontSize: "clamp(22px,1.8vw,28px)", color: "#F4EEE6" }}>
              &ldquo;The house feels resolved — like it was always meant to be this way.&rdquo;
            </p>
            <div className="mt-5 eyebrow" style={{ color: "rgba(244,238,230,0.6)" }}>Private Residence — Whole Home Renovation</div>
          </div>
        </div>
      </section>

      <StartProjectCTA />
      <SiteFooter />
    </div>
  );
}